/**
 * New Assessment Page
 *
 * 3-step wizard for creating manual assessments:
 * 1. Selection - Choose operator and SOP
 * 2. Scoring - Score each SOP step (hierarchical: Jobs → Tasks → Steps)
 * 3. Review - Review category scores and submit
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ClipboardCheck,
  User,
  FileText,
  CheckCircle2,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import { useSop } from '../../hooks/useSOPs';
import { useCreateAssessment } from '../../hooks/useAssessments';
import { SelectionStep } from './components/SelectionStep';
import { ScoringStep } from './components/ScoringStep';
import { ReviewStep } from './components/ReviewStep';
import type { SopDetailSection, SopTaskStep } from '@guild/shared';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';

// ============================================
// TYPES
// ============================================

export interface StepScore {
  passed: boolean;
  score: number;
  feedback: string;
}

export interface AssessmentFormData {
  // Step 1: Selection
  operatorId: string;
  sopId: string;

  // Step 2: Scoring (keyed by sopStepId)
  stepScores: Record<string, StepScore>;

  // Step 3: Review
  complianceScore: number;
  efficiencyScore: number;
  safetyScore: number;
  managerNotes: string;
}

type WizardStep = 1 | 2 | 3;

const PASS_THRESHOLD = 70;

// ============================================
// INITIAL STATE
// ============================================

const initialFormData: AssessmentFormData = {
  operatorId: '',
  sopId: '',
  stepScores: {},
  complianceScore: 0,
  efficiencyScore: 0,
  safetyScore: 0,
  managerNotes: '',
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Flatten all steps from hierarchical SOP structure
 */
export function getAllSteps(sections: SopDetailSection[] | undefined): Array<SopTaskStep & { sectionTitle: string; taskTitle: string; isSafetyCritical: boolean }> {
  if (!sections) return [];

  const steps: Array<SopTaskStep & { sectionTitle: string; taskTitle: string; isSafetyCritical: boolean }> = [];

  for (const section of sections) {
    for (const task of section.tasks || []) {
      for (const step of task.steps || []) {
        steps.push({
          ...step,
          sectionTitle: section.title,
          taskTitle: task.title,
          isSafetyCritical: step.isSafetyCritical || task.isSafetyCritical,
        });
      }
    }
  }

  return steps;
}

/**
 * Calculate category scores from step scores
 */
export function calculateCategoryScores(
  stepScores: Record<string, StepScore>,
  allSteps: Array<SopTaskStep & { isSafetyCritical: boolean }>
): { compliance: number; efficiency: number; safety: number } {
  if (allSteps.length === 0) {
    return { compliance: 0, efficiency: 0, safety: 0 };
  }

  const scoredSteps = allSteps.filter(step => stepScores[step.id]);
  if (scoredSteps.length === 0) {
    return { compliance: 0, efficiency: 0, safety: 0 };
  }

  // Compliance: percentage of steps passed
  const passedCount = scoredSteps.filter(step => stepScores[step.id]?.passed).length;
  const compliance = Math.round((passedCount / scoredSteps.length) * 100);

  // Efficiency: average step score
  const totalScore = scoredSteps.reduce((sum, step) => sum + (stepScores[step.id]?.score || 0), 0);
  const efficiency = Math.round(totalScore / scoredSteps.length);

  // Safety: percentage of safety-critical steps passed
  const safetySteps = scoredSteps.filter(step => step.isSafetyCritical);
  const safety = safetySteps.length > 0
    ? Math.round((safetySteps.filter(step => stepScores[step.id]?.passed).length / safetySteps.length) * 100)
    : 100; // If no safety steps, default to 100

  return { compliance, efficiency, safety };
}

/**
 * Calculate overall score from category scores
 * Formula: (compliance × 0.4) + (efficiency × 0.3) + (safety × 0.3)
 */
export function calculateOverallScore(compliance: number, efficiency: number, safety: number): number {
  return Math.round((compliance * 0.4) + (efficiency * 0.3) + (safety * 0.3));
}

// ============================================
// MAIN COMPONENT
// ============================================

export function NewAssessmentPage() {
  useDocumentTitle('New Assessment');
  const navigate = useNavigate();

  // Wizard state
  const [currentStep, setCurrentStep] = useState<WizardStep>(1);
  const [formData, setFormData] = useState<AssessmentFormData>(initialFormData);
  const [error, setError] = useState<string | null>(null);

  // Fetch SOP data when selected
  const { data: sop, isLoading: sopLoading } = useSop(formData.sopId || null);

  // Create assessment mutation
  const createAssessment = useCreateAssessment();

  // Get all steps from SOP hierarchy
  const allSteps = useMemo(() => getAllSteps(sop?.sections), [sop?.sections]);

  // Calculate scores when step scores change
  const calculatedScores = useMemo(() => {
    return calculateCategoryScores(formData.stepScores, allSteps);
  }, [formData.stepScores, allSteps]);

  // Auto-update category scores when moving to review step
  useEffect(() => {
    if (currentStep === 3) {
      setFormData(prev => ({
        ...prev,
        complianceScore: calculatedScores.compliance,
        efficiencyScore: calculatedScores.efficiency,
        safetyScore: calculatedScores.safety,
      }));
    }
  }, [currentStep, calculatedScores]);

  // ============================================
  // EVENT HANDLERS
  // ============================================

  const handleSelectionChange = useCallback((operatorId: string, sopId: string) => {
    setFormData(prev => ({
      ...prev,
      operatorId,
      sopId,
      // Reset scores when SOP changes
      stepScores: prev.sopId !== sopId ? {} : prev.stepScores,
    }));
    setError(null);
  }, []);

  const handleStepScoreChange = useCallback((stepId: string, score: StepScore) => {
    setFormData(prev => ({
      ...prev,
      stepScores: {
        ...prev.stepScores,
        [stepId]: score,
      },
    }));
  }, []);

  const handleBatchScore = useCallback((stepIds: string[], score: number) => {
    const passed = score >= PASS_THRESHOLD;
    setFormData(prev => {
      const newScores = { ...prev.stepScores };
      for (const id of stepIds) {
        newScores[id] = { passed, score, feedback: '' };
      }
      return { ...prev, stepScores: newScores };
    });
  }, []);

  const handleCategoryScoreChange = useCallback((category: 'compliance' | 'efficiency' | 'safety', value: number) => {
    setFormData(prev => ({
      ...prev,
      [`${category}Score`]: value,
    }));
  }, []);

  const handleNotesChange = useCallback((notes: string) => {
    setFormData(prev => ({ ...prev, managerNotes: notes }));
  }, []);

  // ============================================
  // NAVIGATION
  // ============================================

  const canProceedToStep2 = formData.operatorId && formData.sopId && !sopLoading;

  const scoredStepsCount = Object.keys(formData.stepScores).length;
  const canProceedToStep3 = scoredStepsCount > 0;

  const handleNext = () => {
    if (currentStep === 1 && canProceedToStep2) {
      setCurrentStep(2);
    } else if (currentStep === 2 && canProceedToStep3) {
      setCurrentStep(3);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => (prev - 1) as WizardStep);
    }
  };

  const handleSubmit = async () => {
    setError(null);

    // Validate
    if (!formData.operatorId || !formData.sopId) {
      setError('Please select an operator and SOP');
      return;
    }

    if (Object.keys(formData.stepScores).length === 0) {
      setError('Please score at least one step');
      return;
    }

    try {
      const steps = Object.entries(formData.stepScores).map(([sopStepId, score]) => ({
        sopStepId,
        passed: score.passed,
        score: score.score,
        feedback: score.feedback || undefined,
      }));

      await createAssessment.mutateAsync({
        operatorId: formData.operatorId,
        sopId: formData.sopId,
        complianceScore: formData.complianceScore,
        efficiencyScore: formData.efficiencyScore,
        safetyScore: formData.safetyScore,
        managerNotes: formData.managerNotes || undefined,
        steps,
      });

      // Navigate to assessments list on success
      navigate('/assessments');
    } catch (err) {
      console.error('Create assessment error:', err);
      setError(err instanceof Error ? err.message : 'Failed to create assessment');
    }
  };

  // ============================================
  // RENDER
  // ============================================

  const stepLabels = ['Select', 'Score', 'Review'];
  const stepIcons = [User, FileText, CheckCircle2];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {/* Subtle background pattern */}
      <div className="fixed inset-0 opacity-[0.015] pointer-events-none">
        <svg width="100%" height="100%">
          <defs>
            <pattern id="grid" width="32" height="32" patternUnits="userSpaceOnUse">
              <path d="M 32 0 L 0 0 0 32" fill="none" stroke="currentColor" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>

      <div className="relative max-w-5xl mx-auto px-6 py-8">
        {/* Back navigation */}
        <Link
          to="/assessments"
          className="inline-flex items-center gap-2 text-gray-500 hover:text-[#00674a] transition-colors mb-8 group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          <span className="font-medium">Back to Assessments</span>
        </Link>

        {/* Header */}
        <div className="flex items-center gap-4 mb-10">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#00674a] to-[#00674a]/80 flex items-center justify-center shadow-xl shadow-[#00674a]/20">
            <ClipboardCheck className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
              New Assessment
            </h1>
            <p className="text-gray-500 mt-1">
              Create a manual operator assessment
            </p>
          </div>
        </div>

        {/* Step Indicator */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-8">
          <div className="flex items-center justify-center gap-2 sm:gap-4">
            {[1, 2, 3].map((step, index) => {
              const StepIcon = stepIcons[index];
              const isActive = currentStep === step;
              const isCompleted = currentStep > step;

              return (
                <div key={step} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <div
                      className={`
                        relative w-12 h-12 rounded-xl flex items-center justify-center
                        transition-all duration-500 ease-out
                        ${isActive
                          ? 'bg-[#00674a] text-white scale-110 shadow-lg shadow-[#00674a]/30'
                          : isCompleted
                          ? 'bg-[#c5e8c2] text-[#00674a]'
                          : 'bg-gray-100 text-gray-400'
                        }
                      `}
                    >
                      {isCompleted ? (
                        <CheckCircle2 className="w-6 h-6" />
                      ) : (
                        <StepIcon className="w-5 h-5" />
                      )}
                      {isActive && (
                        <div className="absolute inset-0 rounded-xl ring-4 ring-[#00674a]/20 animate-pulse" />
                      )}
                    </div>
                    <span className={`
                      mt-2 text-xs font-semibold tracking-wide uppercase
                      ${isActive ? 'text-[#00674a]' : isCompleted ? 'text-[#00674a]/70' : 'text-gray-400'}
                    `}>
                      {stepLabels[index]}
                    </span>
                  </div>
                  {step < 3 && (
                    <div className={`
                      w-12 sm:w-20 h-1 mx-2 sm:mx-4 rounded-full transition-colors duration-500
                      ${currentStep > step ? 'bg-[#c5e8c2]' : 'bg-gray-200'}
                    `} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Error display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl flex items-start gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* Content Area */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          {/* Step 1: Selection */}
          {currentStep === 1 && (
            <SelectionStep
              operatorId={formData.operatorId}
              sopId={formData.sopId}
              sop={sop}
              sopLoading={sopLoading}
              onChange={handleSelectionChange}
            />
          )}

          {/* Step 2: Scoring */}
          {currentStep === 2 && sop && (
            <ScoringStep
              sop={sop}
              stepScores={formData.stepScores}
              allSteps={allSteps}
              onStepScoreChange={handleStepScoreChange}
              onBatchScore={handleBatchScore}
            />
          )}

          {/* Step 3: Review */}
          {currentStep === 3 && sop && (
            <ReviewStep
              formData={formData}
              sop={sop}
              allSteps={allSteps}
              calculatedScores={calculatedScores}
              onCategoryScoreChange={handleCategoryScoreChange}
              onNotesChange={handleNotesChange}
            />
          )}

          {/* Footer */}
          <div className="px-8 py-6 bg-gray-50/50 border-t border-gray-100 flex items-center justify-between gap-4">
            <div>
              {currentStep > 1 && (
                <button
                  onClick={handleBack}
                  className="px-6 py-3 text-gray-600 font-semibold hover:text-gray-900 transition-colors"
                >
                  Back
                </button>
              )}
            </div>

            <div className="flex items-center gap-3">
              {currentStep === 2 && (
                <span className="text-sm text-gray-500">
                  <span className="font-semibold text-gray-700">{scoredStepsCount}</span> of{' '}
                  <span className="font-semibold text-gray-700">{allSteps.length}</span> steps scored
                </span>
              )}

              {currentStep < 3 ? (
                <button
                  onClick={handleNext}
                  disabled={
                    (currentStep === 1 && !canProceedToStep2) ||
                    (currentStep === 2 && !canProceedToStep3)
                  }
                  className="px-8 py-3 bg-[#00674a] text-white font-semibold rounded-xl hover:bg-[#005540] disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 shadow-lg shadow-[#00674a]/20"
                >
                  Continue
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={createAssessment.isPending}
                  className="px-8 py-3 bg-[#00674a] text-white font-semibold rounded-xl hover:bg-[#005540] disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 shadow-lg shadow-[#00674a]/20"
                >
                  {createAssessment.isPending ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-5 h-5" />
                      Submit Assessment
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NewAssessmentPage;
